import { Task } from '@/api';
import { ReactElement, useMemo } from 'react';
import styles from './task-status-indicator.module.scss';
import { UIAvatar, UIText, UITooltip } from '@/ui';
import { useCountdownTimer, useTasksTranslation } from '../hooks';

export type TaskStatusIndicatorProps = {
    task: Task;
    filled?: boolean;
};
export function TaskStatusIndicator(
    props: TaskStatusIndicatorProps
): ReactElement {
    const { task, filled = false } = props;
    const { t } = useTasksTranslation();

    const statusText = useMemo(() => {
        switch (task.simpleStatus) {
            case 'ACTIVE':
                return t('components.task_status_indicator.status_active');
            case 'COMPLETED':
                return t('components.task_status_indicator.status_completed');
            case 'ALERTED':
                return t('components.task_status_indicator.status_alerted');
            default:
                return '';
        }
    }, [task.simpleStatus, t]);

    const containerStyle = [styles.container, styles[task.simpleStatus]];

    if (filled) {
        containerStyle.push(styles.filled);
    } else {
        containerStyle.push(styles.unfilled);
    }

    const dueTimeDate = useMemo(() => new Date(task.dueTime), [task.dueTime]);

    const assignNode = useMemo(() => {
        if (
            task.simpleStatus === 'ALERTED' &&
            task.alert &&
            task.alert.assigned
        ) {
            if (
                task.alert.assigned.user.firstName &&
                task.alert.assigned.user.lastName &&
                task.alert.assigned.user.image
            ) {
                // Two whole keys rather than a conditional fragment: a
                // translator cannot place a clause that may not be there.
                const name = `${task.alert.assigned.user.firstName} ${task.alert.assigned.user.lastName}`;
                const tooltipText = task.alert.assigned.isArc
                    ? t('components.task_status_indicator.assigned_to_arc', {
                          name
                      })
                    : t('components.task_status_indicator.assigned_to', {
                          name
                      });
                return (
                    <UITooltip text={tooltipText}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                padding: 4,
                                borderRadius: 8
                            }}
                        >
                            <UIAvatar
                                img={task.alert.assigned.user.image}
                                alt={t(
                                    'components.task_status_indicator.assigned_avatar_alt'
                                )}
                                size={18}
                            />
                            <UIText type={'white'} variant="small">
                                {t('components.task_status_indicator.assigned')}
                            </UIText>
                        </div>
                    </UITooltip>
                );
            } else {
                // Pre-existing: this branch builds an element and drops it —
                // there is no `return`, so this never renders. Left as it is
                // because adding the `return` is a behaviour change, not a
                // translation. Flagged for review.
                <UIText type={'white'} variant="small">
                    {t('components.task_status_indicator.assigned')}
                </UIText>;
            }
        }
    }, [task, t]);

    return (
        <div className={containerStyle.join(' ')}>
            <UIText type={'white'} medium>
                {statusText}
            </UIText>
            {task.simpleStatus === 'ACTIVE' && <Countdown time={dueTimeDate} />}
            {assignNode}
        </div>
    );
}

function Countdown(props: { time: Date }): ReactElement {
    const timer = useCountdownTimer({ timeoutAt: props.time });
    return <UIText type={'white'}>{timer}</UIText>;
}
